import { X } from "lucide-react";
import React, { useEffect, useRef } from "react";

const TABBABLE =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
}) {
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useRef(
    `ne-modal-title-${Math.random().toString(36).slice(2, 9)}`,
  );

  // Hold the latest onClose in a ref so the focus-setup effect below can run
  // ONCE per open (deps: [open]) instead of re-running every render. Parents
  // pass onClose as an inline arrow, so it changes identity on every keystroke;
  // if the effect depended on it, each keystroke would re-focus the first field
  // and yank the caret out — the "one character at a time" bug.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const node = modalRef.current;
    if (!node) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const tabbables = node.querySelectorAll<HTMLElement>(TABBABLE);
    if (tabbables.length > 0) tabbables[0].focus();

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = node.querySelectorAll<HTMLElement>(TABBABLE);
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      previouslyFocused?.focus?.();
    };
    // Intentionally only [open]: onClose is read via onCloseRef so typing in a
    // field inside the modal doesn't re-trigger focus management.
  }, [open]);

  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        ref={modalRef}
        className={wide ? "modal wide" : "modal"}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId.current : undefined}
        aria-label={title ? undefined : "Dialog"}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id={titleId.current}>{title}</h2>
          <button
            className="icon-button"
            onClick={onClose}
            title="Close"
            aria-label="Close dialog"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </div>
  );
}
